import React, { useCallback, useEffect, useRef, useState } from 'react';

import Pagination from '@components/common/Shared/Pagination';
import ProfileListTable from '@components/common/Table/ProfileListTable';
import ProfileSearchBox from './ProfileSearchBox';
import { SectionWrapper } from '@components/Styles/CommonStyle';
import { request } from '@api/axios';
import { useDialog } from '@context/DialogContext';
import { useLoading } from '@context/LoadingContext';
import { useNavigate } from 'react-router-dom';

const ProfileList = () => {
  const navigate = useNavigate();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const { showDialog } = useDialog();
  const isFirstRender = useRef(true);

  // URL의 쿼리 문자열을 파싱하여 초기값 설정
  const queryParams = new URLSearchParams(window.location.search);
  const defaultValues = {
    name: queryParams.get('name') || '',
    grades: queryParams.get('grades') ? queryParams.get('grades').split(',') : [],
    workType: queryParams.get('workType') || '',
    page: Number(queryParams.get('page')) || 1,
    limit: Number(queryParams.get('limit')) || 10,
    orderBy: queryParams.get('orderBy') || 'person_name',
    orderDir: queryParams.get('orderDir') || 'ASC',
  };

  const [profileList, setProfileList] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useState(defaultValues);
  const [selectedProfiles, setSelectedProfiles] = useState([]);

  const handleMoveDetail = useCallback(
    (id) => {
      navigate(`/profile/detail/${id}`);
    },
    [navigate],
  );

  const handleGetProfileList = useCallback(
    async (data, isSearch) => {
      showLoading();
      try {
        if (Array.isArray(data.grades)) {
          data.grades = data.grades.join(',');
        }

        // URLSearchParams 객체 생성
        const queryParams = new URLSearchParams(data);

        if (isSearch) {
          queryParams.set('page', 1);
        }

        // URL 업데이트
        navigate(`?${queryParams.toString()}`, { replace: true });

        const { data: response, status, message } = await request('get', `/api/profile/list?${queryParams}`);

        if (status === 200) {
          setProfileList([...response.list]);
          setTotal(response.totalCnt);
        } else {
          throw new Error(message);
        }
      } catch (error) {
        console.log(error);
        showDialog({ title: '조회 실패', message: '조회에 실패했습니다.\n잠시 후 다시 시도해주세요.' });
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, navigate, showDialog],
  );

  const handleSearch = (data) => {
    const newParams = { ...data, page: 1 };
    setSearchParams(newParams);
    handleGetProfileList(newParams, true);
  };

  const handlePageChange = (value) => {
    //페이지 변경시 전체 선택 해제
    if (isAllOnPageSelected) {
      handleSelectAllProfiles(false);
    }
    const newParams = { ...searchParams, page: value };
    setSearchParams(newParams);
    handleGetProfileList(newParams, false);
  };

  const handleLimitChange = (value) => {
    const newParams = { ...searchParams, limit: value, page: 1 };
    setSearchParams(newParams);
    handleGetProfileList(newParams, false);
  };

  const handleOrderByAndOrderDirChange = (orderBy, orderDir) => {
    const newParams = { ...searchParams, orderBy: orderBy, orderDir: orderDir };
    setSearchParams(newParams);
    handleGetProfileList(newParams, false);
  };

  // 프로필 선택 핸들러
  const handleSelectProfile = useCallback((profileId) => {
    setSelectedProfiles((prevSelected) =>
      prevSelected.includes(profileId) ? prevSelected.filter((id) => id !== profileId) : [...prevSelected, profileId],
    );
  }, []);

  // 선택된 프로필 삭제 핸들러
  const handleDeleteSelectedProfiles = useCallback(async () => {
    if (selectedProfiles.length === 0) {
      showDialog({ title: '알림', message: '삭제할 프로필을 선택해주세요.' });
      return;
    }

    showDialog({
      type: 'confirm',
      title: '프로필 삭제',
      message: `선택된 ${selectedProfiles.length}개의 프로필을 정말 삭제하시겠습니까?`,
      confirmText: '확인',
      cancelText: '취소',
      onConfirm: async () => {
        showLoading();
        try {
          console.log('삭제 요청:', selectedProfiles);

          // TODO: 여기에 실제 삭제 API 호출 로직 구현
          const { status, message } = await request('post', '/api/profile/delete', { data: { ids: selectedProfiles } });
          if (status === 200) {
            showDialog({ title: '삭제 완료', message: '선택된 프로필이 삭제되었습니다.' });
          } else {
            throw new Error(message);
          }

          // 성공적으로 삭제 후 목록 새로고침 및 선택 초기화
          await handleGetProfileList(searchParams, false);
          setSelectedProfiles([]);
          // 성공 알림은 onConfirm 내부에서 다시 showDialog 호출
          showDialog({ title: '삭제 완료', message: '선택된 프로필이 삭제되었습니다.' });
        } catch (error) {
          console.error('삭제 실패:', error);
          // 실패 알림도 onConfirm 내부에서 다시 showDialog 호출
          showDialog({ title: '삭제 실패', message: '프로필 삭제 중 오류가 발생했습니다.' });
        } finally {
          hideLoading();
        }
      },
      onCancel: () => {
        console.log('프로필 삭제 취소됨');
      },
    });
  }, [selectedProfiles, showDialog, showLoading, hideLoading, handleGetProfileList, searchParams]);

  // 전체 선택/해제 핸들러
  const handleSelectAllProfiles = useCallback(
    (checked) => {
      if (checked) {
        // 현재 페이지의 모든 프로필 ID를 선택
        const allProfileIdsOnPage = profileList.map((profile) => profile.profileId);
        setSelectedProfiles(allProfileIdsOnPage);
      } else {
        // 모든 선택 해제
        setSelectedProfiles([]);
      }
    },
    [profileList, setSelectedProfiles],
  );

  // 현재 페이지의 모든 프로필이 선택되었는지 여부
  const isAllOnPageSelected = profileList.length > 0 && selectedProfiles.length === profileList.length;

  useEffect(() => {
    if (isFirstRender.current) {
      handleGetProfileList(searchParams);
      isFirstRender.current = false;
    }
  }, [searchParams, handleGetProfileList]);

  return (
    <React.Fragment>
      <SectionWrapper>
        <ProfileSearchBox searchValues={defaultValues} onSubmit={handleSearch} />
      </SectionWrapper>

      <ProfileListTable
        profileList={profileList}
        total={total} // 총 개수는 API 기준을 유지할지, 화면상 기준으로 변경할지 결정 필요
        page={searchParams.page}
        limit={searchParams.limit}
        onMoveDetail={handleMoveDetail}
        isLoading={isLoading}
        selectedProfiles={selectedProfiles}
        onSelectProfile={handleSelectProfile}
        onDeleteSelected={handleDeleteSelectedProfiles}
        onSelectAll={handleSelectAllProfiles} // onSelectAll prop 추가
        isAllOnPageSelected={isAllOnPageSelected} // isAllOnPageSelected prop 추가
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        handleOrderByAndOrderDirChange={handleOrderByAndOrderDirChange}
        orderBy={searchParams.orderBy}
        orderDir={searchParams.orderDir}
      />
    </React.Fragment>
  );
};

export default ProfileList;
